// src/components/Sidebar.tsx - Fixed sidebar header layout
import React, { memo, useCallback, useState, useEffect } from "react";
import { Patient } from "../types";
import ThemeToggle from "./ThemeToggle";
import AddPatientModal, { PatientFormData } from "./AddPatientForm";
import NurseActivityTracker from "./NurseActivityTracker";
import { useTheme } from "../contexts/ThemeContext";
import { User, LogOut, Pencil, X } from "lucide-react";
import { getAvailableDepartments } from '../utils/departmentFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";


// Edit Patient Modal Component
interface EditPatientModalProps {
  isOpen: boolean;
  patient: Patient | null;
  onClose: () => void;
  onSubmit: (patient: PatientFormData) => Promise<void>;
  showSuccess?: boolean;
}

const EditPatientModal: React.FC<EditPatientModalProps> = ({ isOpen, patient, onClose, onSubmit, showSuccess = false }) => {
  const [formData, setFormData] = useState<PatientFormData>({
    MRN: patient?.MRN || '',
    Name: patient?.Name || '',
    DOB: patient?.DOB || '',
    Department: patient?.Department || '',
    Room: patient?.Room || '',
    Diagnosis: patient?.Diagnosis || '',
    Attending: patient?.Attending || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form data when patient changes
  React.useEffect(() => {
    if (patient) {
      setFormData({
        MRN: patient.MRN,
        Name: patient.Name,
        DOB: patient.DOB,
        Department: patient.Department ?? '',
        Room: patient.Room ?? '',
        Diagnosis: patient.Diagnosis,
        Attending: patient.Attending ?? ''
      });
      // Reset loading and error states when opening for a new patient
      setLoading(false);
      setError(null);
    }
  }, [patient]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onSubmit(formData);
      // Don't close immediately - let parent handle success state
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update patient');
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !patient) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Edit Patient</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="patient-form">
          {error && <div className="form-error">{error}</div>}
          {showSuccess && <div className="form-success">Patient updated successfully!</div>}
          
          <div className="form-group">
            <label htmlFor="edit-MRN">MRN (Medical Record Number)</label>
            <input
              type="text"
              id="edit-MRN"
              name="MRN"
              value={formData.MRN}
              disabled
              className="input-disabled"
            />
            <small className="form-hint">MRN cannot be changed</small>
          </div>

          <div className="form-group">
            <label htmlFor="edit-Name">Patient Name *</label>
            <input
              type="text"
              id="edit-Name"
              name="Name"
              value={formData.Name}
              onChange={handleChange}
              placeholder="Full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-DOB">Date of Birth *</label>
            <input
              type="date"
              id="edit-DOB"
              name="DOB"
              value={formData.DOB}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-Department">Department *</label>
            <select
              id="edit-Department"
              name="Department"
              value={formData.Department}
              onChange={handleChange}
              required
            >
              <option value="ICU">ICU</option>
              <option value="ER">Emergency Room</option>
              <option value="Med-Surg">Med-Surg</option>
              <option value="PACU">PACU</option>
              <option value="NICU">NICU</option>
              <option value="Pediatrics">Pediatrics</option>
              <option value="Cardiology">Cardiology</option>
              <option value="Oncology">Oncology</option>
              <option value="Labor & Delivery">Labor & Delivery</option>
              <option value="Psychiatry">Psychiatry</option>
              <option value="Rehab">Rehab</option>
              <option value="Women's Services (OB)">Women's Services (OB)</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="edit-Room">Room # *</label>
            <input
              type="text"
              id="edit-Room"
              name="Room"
              value={formData.Room}
              onChange={handleChange}
              placeholder="e.g., 302-A"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-Attending">Attending Physician *</label>
            <input
              type="text"
              id="edit-Attending"
              name="Attending"
              value={formData.Attending}
              onChange={handleChange}
              placeholder="e.g., Dr. Sarah Williams"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-Diagnosis">Diagnosis *</label>
            <textarea
              id="edit-Diagnosis"
              name="Diagnosis"
              value={formData.Diagnosis}
              onChange={handleChange}
              placeholder="Primary diagnosis"
              rows={3}
              required
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
interface DeleteConfirmModalProps {
  isOpen: boolean;
  patient: Patient | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ isOpen, patient, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error('Failed to delete patient:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !patient) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content delete-confirm-modal">
        <div className="modal-header">
          <h2>Delete Patient</h2>
        </div>
        <div className="delete-confirm-body">
          <p>Are you sure you want to delete this patient?</p>
          <div className="delete-patient-info">
            <strong>{patient.Name}</strong>
            <span>MRN: {patient.MRN}</span>
          </div>
          <p className="delete-warning">
            This action cannot be undone. All associated activities will also be deleted.
          </p>
        </div>
        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="button" className="btn-delete" onClick={handleConfirm} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Patient'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface NurseStats {
  patientsCount: number;
  activitiesEntered: number;
  handoffsCompleted: number;
  totalTimeInApp: number;
  reportsSubmitted: number;
  otherRecordingsSubmitted: number;
}

interface SidebarProps {
  className?: string;
  patientMRN: string;
  setPatientMRN: (value: string) => void;
  claudeApiKey: string;
  backendAvailable: boolean;
  lookupPatient: (mrn: string) => void;
  samplePatients: Patient[];
  setSamplePatients: (patients: Patient[]) => void;
  resetSession: () => void;
  loggedInUserName: string; // Full name of logged-in user for filtering patients
  loggedInUserEmail: string; // Email/ID of logged-in user
  onLogout: () => void; // Logout handler
  shiftStartTime: Date;
  shiftDuration: number; // Shift duration in hours
  nurseStats: NurseStats;
}


const Sidebar: React.FC<SidebarProps> = memo(({
  className = '',
  patientMRN,
  setPatientMRN,
  claudeApiKey,
  backendAvailable,
  lookupPatient,
  samplePatients,
  setSamplePatients,
  resetSession,
  loggedInUserName,
  loggedInUserEmail,
  onLogout,
  shiftStartTime,
  shiftDuration,
  nurseStats
}) => {
  const { theme } = useTheme();
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [editSuccess, setEditSuccess] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [patientRiskLevels, setPatientRiskLevels] = useState<Record<string, number>>({});
  const [patientsWithHandoffs, setPatientsWithHandoffs] = useState<string[]>([]);
  const [isLoadingRisks, setIsLoadingRisks] = useState(false);

  // Reset all local state when resetSession is called
  const handleResetSession = useCallback(() => {
    // Reset all Sidebar local state
    setShowAddPatientModal(false);
    setIsEditMode(false);
    setShowEditPatientModal(false);
    setShowDeleteConfirmModal(false);
    setSelectedPatient(null);
    setSuccessMessage('');
    setEditSuccess(false);
    setSelectedDepartment('');
    setPatientRiskLevels({});
    setPatientsWithHandoffs([]);
    setIsLoadingRisks(false);
    // Call the parent's resetSession
    resetSession();
  }, [resetSession]);

  // Filter patients by selected department
  const filteredPatients = selectedDepartment 
    ? samplePatients.filter(pt => pt.Department === selectedDepartment)
    : [];

  // Evaluate patient risk levels when department changes - only for patients with handoffs
  useEffect(() => {
    const evaluateRisks = async () => {
      if (!selectedDepartment || filteredPatients.length === 0) {
        setPatientRiskLevels({});
        setPatientsWithHandoffs([]);
        return;
      }

      setIsLoadingRisks(true);
      try {
        const response = await fetch(`${BACKEND_URL}/patients/risk-assessment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patients: filteredPatients.map(pt => ({
              MRN: pt.MRN,
              Name: pt.Name,
              Diagnosis: pt.Diagnosis,
              Room: pt.Room
            })),
            department: selectedDepartment,
            current_nurse_id: loggedInUserEmail
          })
        });

        const data = await response.json();
        if (data.success) {
          setPatientRiskLevels(data.risks || {});
          setPatientsWithHandoffs(data.hasHandoff || []);
        }
      } catch (error) {
        console.error('Failed to evaluate patient risks:', error);
      } finally {
        setIsLoadingRisks(false);
      }
    };

    evaluateRisks();
  }, [selectedDepartment, samplePatients, loggedInUserEmail]);

  // Helper function to get risk color class - only applies to patients with handoffs
  const getRiskColorClass = (mrn: string): string => {
    // If patient doesn't have a handoff, no risk class (use default teal)
    if (!patientsWithHandoffs.includes(mrn)) return '';
    
    const riskScore = patientRiskLevels[mrn];
    if (riskScore === undefined) return ''; // No risk data yet
    if (riskScore >= 75) return 'risk-high';
    if (riskScore >= 50) return 'risk-moderate';
    return 'risk-low';
  };

  const handlePatientMRNChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPatientMRN(e.target.value);
  }, [setPatientMRN]);

  const handleLookupClick = useCallback(() => {
    lookupPatient(patientMRN);
  }, [lookupPatient, patientMRN]);

  const handleAddPatient = async (patientData: PatientFormData) => {
    const response = await fetch(`${BACKEND_URL}/patients/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...patientData,
        added_by: loggedInUserName,
        email: loggedInUserEmail
      })
    });
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Failed to add patient');
    }
    
    // Add the new patient to the bottom of the list
    const newPatient: Patient = {
      MRN: data.patient.MRN,
      Name: data.patient.Name,
      DOB: data.patient.DOB,
      Department: data.patient.Department,
      Room: data.patient.Room || '',
      Diagnosis: data.patient.Diagnosis,
      Attending: data.patient.Attending || '',
      Admission_Date: data.patient.Admission_Date
    };
    
    setSamplePatients([...samplePatients, newPatient]);
    
    // Switch to patient's department if different from current selection
    if (newPatient.Department && newPatient.Department !== selectedDepartment) {
      setSelectedDepartment(newPatient.Department);
    }
    
    // Show success message
    setSuccessMessage(`Patient ${newPatient.Name} added successfully!`);
    setTimeout(() => setSuccessMessage(''), 4000);
    
    // Auto-select the newly added patient
    lookupPatient(newPatient.MRN);
  };

  const handleEditPatient = async (patientData: PatientFormData) => {
    const response = await fetch(`${BACKEND_URL}/patients/${patientData.MRN}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...patientData,
        added_by: loggedInUserName,
        email: loggedInUserEmail
      })
    });
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Failed to update patient');
    }
    
    // Update the patient in the list
    const updatedPatients = samplePatients.map(pt => 
      pt.MRN === patientData.MRN ? {
        ...pt,
        Name: data.patient.Name,
        DOB: data.patient.DOB,
        Department: data.patient.Department,
        Room: data.patient.Room || '',
        Diagnosis: data.patient.Diagnosis,
        Attending: data.patient.Attending || ''
      } : pt
    );
    
    setSamplePatients(updatedPatients);
    
    // Show success toast at bottom and close modal
    setSuccessMessage(`Patient ${data.patient.Name} updated successfully!`);
    setTimeout(() => setSuccessMessage(''), 4000);
    
    // Close modal immediately
    setSelectedPatient(null);
    setShowEditPatientModal(false);
    setEditSuccess(false);
  };

  const handleDeletePatient = async () => {
    if (!selectedPatient) return;
    
    // Include email as query param to ensure user can only delete their own patients
    const response = await fetch(`${BACKEND_URL}/patients/${selectedPatient.MRN}?email=${encodeURIComponent(loggedInUserEmail)}`, {
      method: 'DELETE'
    });
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.message || 'Failed to delete patient');
    }
    
    // Remove the patient from the list
    const updatedPatients = samplePatients.filter(pt => pt.MRN !== selectedPatient.MRN);
    setSamplePatients(updatedPatients);
    setSelectedPatient(null);
  };

  const openEditModal = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowEditPatientModal(true);
  };

  const openDeleteModal = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowDeleteConfirmModal(true);
  };

  return (
    <aside className={`sidebar ${className}`}>
      {/* Success Toast */}
      {successMessage && (
        <div className="success-toast">
          {successMessage}
        </div>
      )}
      
      <div className="sidebar-header">
        <ThemeToggle />
      </div>

      {/* Nurse Info Section */}
      <div className={`nurse-info-sidebar ${theme === 'dark' ? 'dark' : ''}`}>
        <div className="nurse-info-sidebar-content">
          <div className="nurse-avatar-small">
            <User size={20} />
          </div>
          <div className="nurse-details-sidebar">
            <span className="nurse-name-sidebar">{loggedInUserName}</span>
            <span className="nurse-id-sidebar">{loggedInUserEmail}</span>
          </div>
        </div>
        <button className="logout-btn" onClick={onLogout} title="Log out">
          <LogOut size={18} />
        </button>
      </div>

      <NurseActivityTracker
        shiftStartTime={shiftStartTime}
        shiftDuration={shiftDuration}
        nurseStats={nurseStats}
      />
      <hr />
      {/* Department Selection */}
      <div className="department-selection">
        <h3>Select Department</h3>
        <select
          value={selectedDepartment}
          onChange={(e) => {
            setSelectedDepartment(e.target.value);
            // Clear selected patient when department changes
            setPatientMRN('');
          }}
          className="department-dropdown"
        >
          <option value="" disabled={selectedDepartment !== ''}>Select</option>
          {getAvailableDepartments(loggedInUserEmail).map((dept: string) => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>
      </div>
      <hr />
      <div>
        <h3>Patient Lookup</h3>
        <input
          type="text"
          value={patientMRN}
          placeholder="Enter MRN"
          onChange={handlePatientMRNChange}
        />
        <button 
          onClick={handleLookupClick}
          disabled={!patientMRN.trim()}
        >
          Lookup Patient
        </button>
      </div>
      <div>
        <button onClick={handleResetSession}>Reset Session</button>
      </div>
      <hr />
      {selectedDepartment ? (
        <div className="patient-list-section">
          <div className="patient-list-header">
            <h4>Patient List</h4>
            {filteredPatients.length > 0 && (
              <button 
                className="edit-list-btn"
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? 'Done' : 'Edit'}
              </button>
            )}
          </div>
          {isLoadingRisks && (
            <div className="risk-loading-indicator">Evaluating patient risks...</div>
          )}
          {filteredPatients.length > 0 ? (
            filteredPatients.map(pt =>
              <div key={pt.MRN} className={`patient-list-item ${isEditMode ? 'edit-mode' : ''} ${getRiskColorClass(pt.MRN)}`}>
                <button 
                  onClick={() => !isEditMode && lookupPatient(pt.MRN)}
                  className={`mrn-btn ${getRiskColorClass(pt.MRN)}`}
                  disabled={isEditMode}
                >
                  <span className="patient-name-text">{pt.Name} - {pt.MRN}</span>
                </button>
                <div className="patient-actions">
                  <button 
                    className="patient-action-btn edit-btn"
                    onClick={() => openEditModal(pt)}
                    title="Edit patient"
                  >
                    <Pencil size={16} />
                  </button>
                  <button 
                    className="patient-action-btn delete-btn"
                    onClick={() => openDeleteModal(pt)}
                    title="Delete patient"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className="no-patients-message">
              <p>No patients in {selectedDepartment}.</p>
              <p>Add a patient to your list to get started.</p>
            </div>
          )}
          <button 
            className="add-patient-btn"
            onClick={() => setShowAddPatientModal(true)}
          >
            + Add Patient
          </button>
        </div>
      ) : (
        <div className="patient-list-section">
          <div className="select-department-message">
            <p>Please select a department above to view your patients.</p>
          </div>
        </div>
      )}

      {/* Add Patient Modal */}
      <AddPatientModal
        isOpen={showAddPatientModal}
        onClose={() => setShowAddPatientModal(false)}
        onSubmit={handleAddPatient}
        onAddByMRN={(patient) => {
          // Add the patient to the list and auto-select
          const newPatient: Patient = {
            MRN: patient.MRN,
            Name: patient.Name,
            DOB: patient.DOB,
            Department: patient.Department,
            Room: patient.Room || '',
            Diagnosis: patient.Diagnosis,
            Attending: patient.Attending || '',
            Admission_Date: (patient as Patient).Admission_Date
          };
          setSamplePatients([...samplePatients, newPatient]);
          // Switch to patient's department if different from current selection
          if (newPatient.Department && newPatient.Department !== selectedDepartment) {
            setSelectedDepartment(newPatient.Department);
          }
          setSuccessMessage(`Patient ${newPatient.Name} added to your list!`);
          setTimeout(() => setSuccessMessage(''), 4000);
          lookupPatient(newPatient.MRN);
        }}
        defaultDepartment={selectedDepartment}
        loggedInUserName={loggedInUserName}
        loggedInUserEmail={loggedInUserEmail}
      />

      {/* Edit Patient Modal */}
      <EditPatientModal
        isOpen={showEditPatientModal}
        patient={selectedPatient}
        onClose={() => {
          setShowEditPatientModal(false);
          setSelectedPatient(null);
          setEditSuccess(false);
        }}
        onSubmit={handleEditPatient}
        showSuccess={editSuccess}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirmModal}
        patient={selectedPatient}
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setSelectedPatient(null);
        }}
        onConfirm={handleDeletePatient}
      />
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;